import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Phone, Stethoscope, Ambulance, Heart, Shield, MapPin, CheckCircle, X, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';

const HELP_LOGO = "https://customer-assets.emergentagent.com/job_316c0f31-5a86-43b3-bcc3-d5c9be92d49a/artifacts/tawy0n3k_images-7.png";

function PlanDescription({ text }) {
  const [expanded, setExpanded] = useState(false);
  const lines = text.split('\n').filter(l => l.trim());
  const isLong = lines.length > 2 || text.length > 120;

  const visibleLines = expanded ? lines : lines.slice(0, 2);

  return (
    <div className="mb-4">
      {lines.length > 1 ? (
        <ul className="space-y-1.5">
          {visibleLines.map((line, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-gray-500">
              <CheckCircle className="w-3.5 h-3.5 text-[#ffff00] shrink-0 mt-0.5" />
              <span>{line.trim()}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className={`text-sm text-gray-500 ${!expanded && isLong ? 'line-clamp-2' : ''}`}>{text}</p>
      )}
      {isLong && (
        <button onClick={() => setExpanded(!expanded)} className="text-xs text-[#000000] font-bold mt-2 flex items-center gap-0.5 hover:underline">
          {expanded ? <><ChevronUp className="w-3.5 h-3.5" /> Ver menos</> : <><ChevronDown className="w-3.5 h-3.5" /> Ver más</>}
        </button>
      )}
    </div>
  );
}

function ContactModal({ open, onClose, partnerName, partnerSlug, planInterest }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [sending, setSending] = useState(false);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error('Completa nombre, correo y teléfono');
      return;
    }
    setSending(true);
    try {
      await api.post('/partners/leads', {
        ...form,
        contact_type: 'plan',
        partner_slug: partnerSlug,
        plan_interest: planInterest || '',
      });
      toast.success('Mensaje enviado. Te contactaremos pronto.');
      onClose();
      setForm({ name: '', email: '', phone: '' });
    } catch {
      toast.error('Error al enviar. Intenta de nuevo.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="partner-contact-modal">
      <div className="bg-white rounded-2xl w-full max-w-md p-6 relative">
        <button onClick={onClose} className="absolute top-4 right-4 p-1 hover:bg-gray-100 rounded-full">
          <X className="w-5 h-5 text-gray-400" />
        </button>
        <h3 className="text-xl font-bold text-[#000000] mb-1">Contactar a {partnerName}</h3>
        <p className="text-sm text-gray-500 mb-5">Envía tus datos y responderemos a tu correo electrónico con todos los antecedentes.</p>
        <form onSubmit={handleSubmit} className="space-y-4">
          {planInterest && (
            <div>
              <label className="block text-sm font-semibold text-[#000000] mb-1">Plan</label>
              <div className="w-full border-2 border-[#ffff00] bg-[#ffff00]/5 rounded-xl px-4 py-3 text-sm font-bold text-[#000000]">
                {planInterest}
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-semibold text-[#000000] mb-1">Nombre</label>
            <input type="text" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#ffff00] transition-colors"
              placeholder="Tu nombre completo" data-testid="lead-name" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-[#000000] mb-1">Correo electrónico</label>
            <input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
              className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#ffff00] transition-colors"
              placeholder="tu@correo.cl" data-testid="lead-email" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-[#000000] mb-1">Teléfono</label>
            <div className="flex">
              <span className="bg-gray-100 border-2 border-r-0 border-gray-200 rounded-l-xl px-3 py-3 text-sm text-gray-500 font-medium">+56</span>
              <input type="tel" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                className="flex-1 border-2 border-gray-200 rounded-r-xl px-4 py-3 text-sm focus:outline-none focus:border-[#ffff00] transition-colors"
                placeholder="9 1234 5678" data-testid="lead-phone" />
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancelar</Button>
            <Button type="submit" disabled={sending}
              className="flex-1 bg-[#ffff00] hover:bg-[#e6e600] text-[#000000] font-bold"
              data-testid="lead-submit">
              {sending ? 'Enviando...' : 'Enviar mensaje'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function HelpRescatePage() {
  const [showContact, setShowContact] = useState(false);
  const [planInterest, setPlanInterest] = useState('');
  const [convenio, setConvenio] = useState(null);

  useEffect(() => {
    api.get('/partners/convenios').then(res => {
      const c = res.data.find(c => c.slug === 'help-rescate');
      if (c) setConvenio(c);
    }).catch(() => {});
  }, []);

  const openContact = (plan) => {
    setPlanInterest(plan || '');
    setShowContact(true);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <div className="bg-white border-b">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link to="/" className="inline-flex items-center gap-2 text-[#000000] hover:text-[#ffff00] mb-6 font-medium text-sm">
            <ArrowLeft className="w-4 h-4" /> Volver al Inicio
          </Link>
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="w-48 shrink-0">
              <img src={HELP_LOGO} alt="Help Rescate" className="w-full" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <h1 className="text-3xl font-bold text-[#000000]">Help Rescate</h1>
                <span className="bg-[#ffff00] text-[#000000] text-xs font-bold px-3 py-1 rounded-full">Convenio SeniorClub</span>
              </div>
              <p className="text-gray-600 leading-relaxed">
                En Help Rescate llevamos más de 25 años acompañando a las personas en momentos donde la salud se vuelve una prioridad. Nuestra misión es estar presentes cuando más se necesita, entregando apoyo, orientación y soluciones concretas frente a emergencias o urgencias de salud que puedan afectar a ti o a tu familia.
              </p>
              <div className="flex items-center gap-4 mt-4">
                <div className="flex items-center gap-1.5 text-sm text-gray-500">
                  <MapPin className="w-4 h-4 text-[#ffff00]" />
                  Valparaíso, Metropolitana y Biobío
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Services */}
        {convenio?.services?.length > 0 && (
        <div className="mb-10">
          <h2 className="text-2xl font-bold text-[#000000] mb-6">Servicios y actividades disponibles</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {convenio.services.map((cat, ci) => (
              <div key={ci} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
                <h3 className="font-bold text-[#000000] text-lg mb-4 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-[#ffff00]" /> {cat.name}
                </h3>
                <ul className="space-y-3">
                  {cat.items.map((s, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                      <CheckCircle className="w-4 h-4 text-[#ffff00] shrink-0" /> {s}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        )}

        {/* Plans */}
        {convenio?.plans?.length > 0 && (
        <div className="mb-10">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-[#000000]">Planes Disponibles</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {convenio.plans.map((plan, i) => (
              <div key={i} className={`bg-white rounded-2xl border-2 ${plan.popular ? 'border-[#ffff00]' : 'border-gray-100 hover:border-[#ffff00]'} transition-colors overflow-hidden shadow-sm relative`}>
                {plan.popular && (
                  <div className="absolute top-0 left-0 right-0 bg-[#ffff00] text-center py-1">
                    <span className="text-xs font-bold text-[#000000]">Más popular</span>
                  </div>
                )}
                <div className={`p-6 ${plan.popular ? 'pt-10' : ''}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xl font-bold text-[#000000]">{plan.name}</h3>
                    {plan.category && <span className="bg-[#ffff00]/10 text-[#000000] text-xs font-bold px-3 py-1 rounded-full">{plan.category}</span>}
                  </div>
                  {plan.description && <PlanDescription text={plan.description} />}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-2xl font-bold text-[#000000]">Desde {plan.price} <span className="text-sm font-normal text-gray-400">/mes</span></p>
                    {plan.uf && <p className="text-xs text-gray-400">Cobro en {plan.currency || 'UF'}, Desde {plan.uf}</p>}
                  </div>
                </div>
                <div className="border-t px-6 py-4">
                  <Button onClick={() => openContact(plan.name)} className={`w-full font-bold ${plan.popular ? 'bg-[#ffff00] hover:bg-[#e6e600] text-[#000000]' : 'bg-[#000000] hover:bg-[#4a5568] text-white'}`} data-testid={`contact-plan-${i}`}>
                    Contactar por {plan.name}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
        )}

        {/* About */}
        <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm">
          <h2 className="text-xl font-bold text-[#000000] mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#ffff00]" /> Sobre Help Rescate
          </h2>
          <p className="text-gray-600 leading-relaxed text-sm">
            Brindamos un servicio integral que combina contención humana con respaldo profesional, a través de un equipo altamente calificado preparado para actuar en situaciones complejas, entregando tranquilidad y apoyo oportuno. Contamos con: 1) Orientación profesional 2) Atención telefónica especializada 3) Rescate móvil en casos que lo requieren 4) Equipamiento y personal de salud capacitado. Hoy estamos presentes en las regiones de Valparaíso, Metropolitana y Biobío, ofreciendo planes tanto para personas como para empresas que buscan protección y acompañamiento real en momentos difíciles. Porque cuando ocurre una urgencia, lo más importante es saber que no estás solo.
          </p>
        </div>
      </div>

      <ContactModal
        open={showContact}
        onClose={() => setShowContact(false)}
        partnerName="Help Rescate"
        partnerSlug="help-rescate"
        planInterest={planInterest}
      />
    </div>
  );
}
